import { useCallback, useEffect, useMemo, useState } from "react";
import { Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Button,
  Card,
  Dialog,
  EmptyState,
  LoadingState,
  SectionHeader,
  Select,
  Table,
  Tbody,
  Td,
  Textarea,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { createFinanceApi } from "../api";
import { formatLabel } from "../labels";
import type { RefundCustomerStatusHistory, RefundDto } from "../types";

/**
 * What the customer is told about a provider refund (i37), and every change
 * to it. Display only: changing it never touches the refund amount, its Xendit
 * state, the payment, the booking or the ledger — the API writes three
 * columns and the database refuses anything more in the same update.
 *
 * Only vetted statuses can be chosen, so nothing shown to the customer can
 * claim an action nobody took. The note is internal: it goes to the audit
 * trail and is never shown to the customer.
 */
export function RefundCustomerStatusPanel({
  refund,
  onChanged,
}: {
  refund: RefundDto;
  onChanged: () => void;
}) {
  const client = useApiClient();
  const api = useMemo(() => createFinanceApi(client), [client]);
  const toast = useToast();
  const [data, setData] = useState<RefundCustomerStatusHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(refund.customer_status ?? "processing");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.getRefundCustomerStatus(refund.id));
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [api, refund.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const completed = refund.status === "settled";
  const unchanged = status === (refund.customer_status ?? "processing");

  return (
    <Card>
      <Stack gap={12}>
        <SectionHeader
          title="What the customer sees"
          description="Separate from the refund itself. Changing it moves no money."
          {...(!completed
            ? {
                actions: (
                  <Button variant="secondary" onClick={() => setOpen(true)}>
                    Change customer status
                  </Button>
                ),
              }
            : {})}
        />
        <Text role="body" as="p">
          “{refund.customer_message ?? "—"}”
        </Text>
        {refund.customer_status_updated_at ? (
          <Text role="caption" as="p">
            Last changed {refund.customer_status_updated_at} by {refund.customer_status_updated_by_user_id ?? "—"}
          </Text>
        ) : null}

        {loading ? (
          <LoadingState message="Loading history…" />
        ) : error ? (
          <Alert tone="danger" title="History unavailable">
            {error}{" "}
            <Button variant="secondary" onClick={() => void load()}>
              Retry
            </Button>
          </Alert>
        ) : !data || data.history.length === 0 ? (
          <EmptyState title="No changes yet" description="The customer sees the default message." />
        ) : (
          <Table>
            <Thead>
              <Tr>
                <Th>When</Th>
                <Th>From → to</Th>
                <Th>By</Th>
                <Th>Internal note</Th>
                <Th>Refund state then</Th>
                <Th>Xendit ids</Th>
              </Tr>
            </Thead>
            <Tbody>
              {data.history.map((h) => (
                <Tr key={h.id}>
                  <Td>{h.changed_at}</Td>
                  <Td>
                    {formatLabel(String(h.previous_status ?? "—"))} → {formatLabel(String(h.new_status ?? "—"))}
                  </Td>
                  <Td>{h.actor_user_id ?? "—"}</Td>
                  <Td>{h.internal_note ?? "—"}</Td>
                  <Td>
                    {formatLabel(String(h.refund_state_at_change ?? "—"))} · payment {formatLabel(String(h.payment_state_at_change ?? "—"))}
                  </Td>
                  <Td>
                    {[h.provider_refund_id, h.provider_reference_id, h.provider_payment_request_id].filter(Boolean).join(" · ") || "—"}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Stack>

      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
          setSaveError(null);
        }}
        title="Change what the customer sees"
        actions={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)} disabled={busy}>
              Cancel
            </Button>
            <Button
              disabled={busy || unchanged}
              onClick={async () => {
                setBusy(true);
                setSaveError(null);
                try {
                  await api.setRefundCustomerStatus(refund.id, {
                    customer_status: status,
                    ...(note.trim() ? { internal_note: note.trim() } : {}),
                  });
                  toast.push({ title: "Customer status updated", tone: "success" });
                  setOpen(false);
                  setNote("");
                  onChanged();
                  await load();
                } catch (err) {
                  setSaveError(apiErrorMessage(err));
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? "Saving…" : "Save"}
            </Button>
          </>
        }
      >
        <Stack gap={12}>
          <Select
            label="Customer sees"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            options={(data?.options ?? []).map((o) => ({ value: o.code, label: o.message }))}
          />
          <Textarea
            label="Internal note (never shown to the customer)"
            value={note}
            maxLength={500}
            onChange={(e) => setNote(e.target.value)}
          />
          <Text role="caption" as="p">
            This does not change the refund amount, its Xendit status, the payment, the booking or the ledger.
          </Text>
          {saveError ? <Alert tone="danger" title="Not saved">{saveError}</Alert> : null}
        </Stack>
      </Dialog>
    </Card>
  );
}
